import { useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, XCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { apiMutate } from "~/utils/apiClient";

interface CohortAccessRequest {
  id: string;
  created_by: string;
  updated_by: string | null;
  cohort_id: number;
  parent_dataset_id: string;
  intended_use: string;
  status: "pending" | "approved" | "denied";
  created_at: string;
  updated_at: string;
}

const STATUS_VARIANT = {
  pending: "warning",
  approved: "success",
  denied: "destructive",
} as const;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

export const DatasetAccessRequests = () => {
  const { datasetId } = useParams();
  const { showSuccess, showError } = useToast();

  const params = new URLSearchParams();
  if (datasetId) params.set("parent_dataset_id", datasetId);

  const {
    data: requests,
    isLoading,
    mutate,
  } = useFetcher<CohortAccessRequest[]>(
    datasetId ? `/api/cohort-explorer/v1/access-requests?${params.toString()}` : null
  );

  const [confirmAction, setConfirmAction] = useState<{
    request: CohortAccessRequest;
    status: "approved" | "denied";
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStatusUpdate = async () => {
    if (!confirmAction) return;
    setIsSubmitting(true);
    try {
      await apiMutate(
        `/api/cohort-explorer/v1/access-requests/${confirmAction.request.id}/status`,
        "PATCH",
        { status: confirmAction.status }
      );
      await mutate();
      showSuccess(
        `Request ${confirmAction.status === "approved" ? "approved" : "denied"} successfully`
      );
    } catch {
      showError(`Failed to ${confirmAction.status === "approved" ? "approve" : "deny"} request`);
    } finally {
      setIsSubmitting(false);
      setConfirmAction(null);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold">Access Requests</h2>
          <p className="text-sm text-muted-foreground">
            Review and manage requests for access to cohorts in this dataset.
          </p>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="rounded-lg border border-border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[20%]">Requested By</TableHead>
                  <TableHead className="w-[8%]">Cohort</TableHead>
                  <TableHead className="w-[32%]">Intended Use</TableHead>
                  <TableHead className="w-[10%]">Status</TableHead>
                  <TableHead className="w-[15%]">Date</TableHead>
                  <TableHead className="w-[15%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">{request.created_by}</TableCell>
                    <TableCell>{request.cohort_id}</TableCell>
                    <TableCell className="whitespace-normal break-words">
                      {request.intended_use}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[request.status]} className="capitalize">
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(request.created_at)}
                    </TableCell>
                    <TableCell>
                      {request.status === "pending" && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-700 hover:text-green-800"
                            onClick={() => setConfirmAction({ request, status: "approved" })}
                          >
                            <CheckCircle className="size-4" />
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive/80"
                            onClick={() => setConfirmAction({ request, status: "denied" })}
                          >
                            <XCircle className="size-4" />
                            Deny
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No cohort access requests for this dataset.
            </p>
          </div>
        )}
      </div>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && !isSubmitting && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.status === "approved" ? "Approve" : "Deny"} Access Request
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {confirmAction?.status === "approved" ? "approve" : "deny"}{" "}
              the access request from <strong>{confirmAction?.request.created_by}</strong> for
              cohort <strong>{confirmAction?.request.cohort_id}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStatusUpdate}
              disabled={isSubmitting}
              className={cn(
                confirmAction?.status === "denied" && "bg-destructive hover:bg-destructive/90"
              )}
            >
              {isSubmitting
                ? "Processing…"
                : confirmAction?.status === "approved"
                  ? "Approve"
                  : "Deny"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
